import { Balloon } from '../interfaces/Ballooning';
import { isNominalNumber } from './nominal';

type ReviewState = Pick<Balloon, 'is_auto_generated' | 'is_reviewed'>;
type SkipReviewState = Pick<Balloon, 'skip_review'>;

export const REVIEW_STATE_FIELDS = [
	'is_reviewed',
	'reviewed_by',
	'reviewed_by_name',
	'reviewed_at',
] as const;

export const isUnreviewedAutoBalloon = (balloon: ReviewState): boolean =>
	!!balloon.is_auto_generated && balloon.is_reviewed === false;

export const countUnreviewed = (balloons: ReviewState[]): number =>
	balloons.filter(isUnreviewedAutoBalloon).length;

type QueueEntry = ReviewState &
	Pick<Balloon, 'id' | 'balloon_number' | 'ballooning_page_number'>;

/** Where a balloon sits in review order: page, then balloon number. */
export type ReviewPosition = { page: number; number: number };

export const reviewPosition = (
	balloon: Pick<Balloon, 'balloon_number' | 'ballooning_page_number'>
): ReviewPosition => ({
	page: balloon.ballooning_page_number ?? 0,
	number: balloon.balloon_number,
});

const comparePositions = (a: ReviewPosition, b: ReviewPosition) =>
	a.page - b.page || a.number - b.number;

/** Unreviewed auto-generated balloons in the order they are reviewed. */
export const reviewQueue = <T extends QueueEntry>(balloons: T[]): T[] =>
	balloons
		.filter(isUnreviewedAutoBalloon)
		.sort((a, b) => comparePositions(reviewPosition(a), reviewPosition(b)));

/**
 * The balloon to review once the one at `from` leaves the queue: the first at or after
 * that spot (deleting with renumbering moves the next balloon into it), wrapping round
 * to the start.
 */
export const nextInQueue = <T extends QueueEntry>(
	queue: T[],
	from: ReviewPosition | null
): T | null => {
	if (!queue.length) return null;
	if (!from) return queue[0];
	return queue.find((b) => comparePositions(reviewPosition(b), from) >= 0) ?? queue[0];
};

/**
 * A row edited on its own. `false` (not `undefined`) is deliberate: it tells a
 * later bulk action that this row already carries a review-worthy edit.
 */
export const markIndividualEdit = <T extends SkipReviewState>(balloon: T): T => ({
	...balloon,
	skip_review: false,
});

/**
 * A row changed only by a bulk action. The server does not count those saves as a
 * review, unless the row was also edited on its own.
 */
export const markBulkEdit = <T extends SkipReviewState>(balloon: T): T => ({
	...balloon,
	skip_review: balloon.skip_review !== false,
});

/** A copy of an auto-generated balloon is a manual balloon; drop the AI provenance. */
export const stripAutoGeneration = <T extends Partial<Balloon>>(balloon: T): T => ({
	...balloon,
	is_auto_generated: false,
	is_reviewed: true,
	reviewed_by: null,
	reviewed_by_name: null,
	reviewed_at: null,
	ai_confidence: null,
	ai_raw_text: null,
	ai_strategy_reason: null,
	feature_bbox: null,
	skip_review: undefined,
});

/** The fields an approval saves along with the review; position and number wait for Save. */
export const MEASUREMENT_FIELDS = [
	'nominal',
	'nominal_decimals',
	'tolerance_minus',
	'tolerance_plus',
	'tolerance_source',
	'measurement_strategy',
	'measurement_type',
	'gage_type',
	'text_value',
	'view',
	'keyence_device',
] as const;

type MeasurementField = (typeof MEASUREMENT_FIELDS)[number];
type MeasurementRow = Pick<Balloon, 'id' | MeasurementField>;
export type MeasurementEdit = { id: string } & Record<MeasurementField, unknown>;

/** A row's measurement fields as the server stores them. */
export const measurementPayload = (b: Pick<Balloon, MeasurementField>) => ({
	// Half-typed text such as "-" or "." is not a number the server can store.
	nominal: isNominalNumber(b.nominal) ? b.nominal : null,
	nominal_decimals: isNominalNumber(b.nominal) ? (b.nominal_decimals ?? null) : null,
	tolerance_minus: b.tolerance_minus,
	tolerance_plus: b.tolerance_plus,
	tolerance_source: b.tolerance_source ?? null,
	measurement_strategy: b.measurement_strategy,
	measurement_type: b.measurement_type,
	gage_type: b.gage_type,
	text_value: b.text_value,
	view: b.view,
	keyence_device: b.keyence_device,
});

// Loose equality, like the unsaved-changes check: "1.000" and 1 are the same value.
const measurementChanged = (local: MeasurementRow, saved: MeasurementRow) =>
	MEASUREMENT_FIELDS.some((field) => (local[field] ?? null) != (saved[field] ?? null));

/**
 * The unsaved measurement edits to the balloons being approved. Each carries every
 * measurement field: the server reads a missing field as a cleared one.
 */
export const approvalEdits = (
	ids: string[],
	local: MeasurementRow[],
	saved: MeasurementRow[]
): MeasurementEdit[] =>
	ids.flatMap((id) => {
		const row = local.find((b) => b.id === id);
		const savedRow = saved.find((b) => b.id === id);
		if (!row || !savedRow || !measurementChanged(row, savedRow)) return [];
		return [{ id, ...measurementPayload(row) }];
	});

/** Record that these rows' measurement fields are saved, so they no longer read as unsaved. */
export const markMeasurementsSaved = <T extends MeasurementRow>(
	saved: T[],
	sent: MeasurementRow[]
): T[] => {
	const byId = new Map(sent.map((b) => [b.id, b]));
	return saved.map((balloon) => {
		const row = byId.get(balloon.id);
		if (!row) return balloon;
		const next = { ...balloon };
		MEASUREMENT_FIELDS.forEach((field) => {
			(next as MeasurementRow)[field] = row[field] as never;
		});
		return next;
	});
};

/** Copy the server's review state onto matching local rows, keeping unsaved edits. */
export const mergeReviewState = <T extends Partial<Balloon>>(
	balloons: T[],
	approved: Partial<Balloon>[]
): T[] => {
	const byId = new Map(approved.map((b) => [b.id, b]));
	return balloons.map((balloon) => {
		const fresh = balloon.id ? byId.get(balloon.id) : undefined;
		if (!fresh) return balloon;
		const next = { ...balloon };
		REVIEW_STATE_FIELDS.forEach((field) => {
			(next as Partial<Balloon>)[field] = fresh[field] as never;
		});
		return next;
	});
};
